import { digestStringSHA256 } from '~/common/utils/hash';
import digitalData from '~/common/services/data-layer-service';
import { EnquiryForm, EnquiryFormValues } from './types';
import {
  EnquiryUserData,
  EnquiryVehicle,
  buildEnquirySubmittedEvent,
  valueOfKind,
  withoutEmpty,
} from './analytics-event';
import { stashEnquiryEvent, takeEnquiryEvent } from './analytics-handoff';

export type { EnquiryVehicle } from './analytics-event';

/**
 * The GA4 event for a submitted offer enquiry - MP-04, "Make an Enquiry".
 *
 * Deliberately the same event the existing enquiry page fires from its thank-you screen
 * (Views/OfferEnquiry/OfferEnquirySent.cshtml): the same name, the same stage, the same hashed user fields,
 * pushed with common data excluded, and - since the confirmation became a page of its own - fired from the
 * same place in the journey. Analytics compares the two, so a difference here would read as a change in
 * customer behaviour rather than a change in our code.
 *
 * What is new is form.details.vehicleSelected, which the redesigned page can supply because it knows which
 * offer is on screen.
 *
 * Which values are taken is decided by the form definition, not by field names hardcoded here: an editor can
 * rename a question, and the kinds (email, mobile, postcode) are what the offers API guarantees.
 *
 * The work is split across the redirect. The offer page has the answers and builds the event; the thank you
 * page pushes it. See analytics-handoff.ts for why it is not pushed before navigating.
 */

/**
 * Builds the event and leaves it for the thank you page.
 *
 * Hashing is SHA-256 hex, the same digest the rest of the site sends, so the same person hashes to the same
 * value whichever form they used. `encryptUserData` is the site's own global from cookieless-world.js and is
 * called when present - the existing enquiry page sends its output alongside the hashes, and dropping it here
 * would quietly send analytics less than the page it replaces. Both happen here, on the page that has the
 * customer's answers, so nothing but the finished event crosses the navigation.
 *
 * Nothing here is allowed to fail the submission. The enquiry has already been published by the time this
 * runs; losing a measurement is a nuisance, losing the customer's confirmation is not acceptable - which is
 * also why the caller must not wait on anything slow here before redirecting.
 */
export async function handOffEnquirySubmitted(
  form: EnquiryForm,
  values: EnquiryFormValues,
  offerUniqueName: string,
  vehicle?: EnquiryVehicle,
  /** What the server minted for this lead, reported so CDP can tie the two together. */
  externalLeadId?: string
): Promise<void> {
  try {
    const email = valueOfKind(form, values, 'email');
    const phone = valueOfKind(form, values, 'mobile');
    const postcode = valueOfKind(form, values, 'postcode');

    const user: EnquiryUserData = {
      emailHashed: email ? await digestStringSHA256(email) : undefined,
      phoneHashed: phone ? await digestStringSHA256(phone) : undefined,
      postcode,
    };

    const event = buildEnquirySubmittedEvent(user, vehicle, externalLeadId);

    const encryptUserData = window['encryptUserData'] as
      | ((input: Record<string, string>) => Promise<Record<string, string>>)
      | undefined;

    if (typeof encryptUserData === 'function') {
      const encrypted = await encryptUserData(
        withoutEmpty({
          encryptedEmail: email,
          encryptedPhone: phone,
          encryptedFirstName: valueOfKind(form, values, 'firstName'),
          encryptedLastName: valueOfKind(form, values, 'lastName'),
        })
      );

      event.user = {
        ...(event.user as Record<string, string>),
        ...encrypted,
      };
    }

    stashEnquiryEvent(sessionStorage, {
      offerUniqueName,
      at: Date.now(),
      event,
    });
  } catch (error) {
    // Swallowed on purpose - see the note above about not failing a submission that already succeeded.
    console.warn(
      'Could not prepare the enquiry submission for analytics',
      error
    );
  }
}

/**
 * Fires the event the offer page left behind, once.
 *
 * Called by the thank you page. Nothing is reported when there is no handover: someone who opens the page
 * from a bookmark, or reloads it, has not submitted an enquiry, and a conversion that counts page views is
 * worse than no conversion at all.
 *
 * Common data excluded, as the existing enquiry page does: the page-level data is already in the layer, and
 * pushing it again with a form event double counts it.
 */
export function pushHandedOffEnquiry(offerUniqueName: string): boolean {
  try {
    const event = takeEnquiryEvent(sessionStorage, offerUniqueName);
    if (!event) return false;

    digitalData.pushWithCommonData(event as never, true);
    return true;
  } catch (error) {
    console.warn('Could not report the enquiry submission to analytics', error);
    return false;
  }
}
